import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Upload, Calendar, Gift, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OCCASIONS = [
  'Akshaya Tritiya',
  'Diwali',
  'Gudi Padwa',
  'Navratri',
  'Ganesh Chaturthi',
  'Dussehra',
  'New Year',
  'Independence Day',
  'Republic Day',
  'Holi',
  'Christmas',
  'Eid',
  'Special Offer',
  'Limited Time',
  'Other'
];

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    occasion: '',
    flyer_image: '',
    discount_text: '',
    valid_from: '',
    valid_until: '',
    is_active: true,
    show_on_homepage: true,
    cta_text: 'Enquire Now',
    cta_link: '',
    terms_conditions: '',
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/offers/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      occasion: '',
      flyer_image: '',
      discount_text: '',
      valid_from: '',
      valid_until: '',
      is_active: true,
      show_on_homepage: true,
      cta_text: 'Enquire Now',
      cta_link: '',
      terms_conditions: '',
    });
    setShowModal(true);
  };

  const openEditModal = (offer) => {
    setEditingId(offer.id);
    setFormData({
      title: offer.title || '',
      description: offer.description || '',
      occasion: offer.occasion || '',
      flyer_image: offer.flyer_image || '',
      discount_text: offer.discount_text || '',
      valid_from: offer.valid_from || '',
      valid_until: offer.valid_until || '',
      is_active: offer.is_active !== false,
      show_on_homepage: offer.show_on_homepage !== false,
      cta_text: offer.cta_text || 'Enquire Now',
      cta_link: offer.cta_link || '',
      terms_conditions: offer.terms_conditions || '',
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const token = localStorage.getItem('admin_token');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, flyer_image: response.data.url });
      toast.success('Flyer uploaded successfully');
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Failed to upload flyer');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingId) {
        await axios.put(`${API}/offers/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Offer updated successfully');
      } else {
        await axios.post(`${API}/offers`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Offer created successfully');
      }
      setShowModal(false);
      fetchOffers();
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error('Failed to save offer');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/offers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Offer deleted');
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer');
    }
  };

  const toggleActive = async (offer) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/offers/${offer.id}`, 
        { is_active: !offer.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchOffers();
      toast.success(`Offer ${!offer.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update offer');
    }
  };

  const isOfferActive = (offer) => {
    if (!offer.is_active) return false;
    const today = new Date().toISOString().split('T')[0];
    return offer.valid_from <= today && offer.valid_until >= today;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <span className="text-xl font-bold text-gray-900">Infinity Group</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">Dashboard</Link>
              <Link to="/admin/projects" className="text-gray-600 hover:text-gray-900">Projects</Link>
              <Link to="/admin/offers" className="text-purple-600 font-medium">Offers</Link>
              <Link to="/admin/testimonials" className="text-gray-600 hover:text-gray-900">Testimonials</Link>
              <Link to="/admin/settings" className="text-gray-600 hover:text-gray-900">Settings</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Promotional Offers & Flyers</h1>
            <p className="text-gray-500 mt-1">Create festive offers for Diwali, Akshaya Tritiya, and more</p>
          </div>
          <Button onClick={openAddModal} className="bg-purple-600 hover:bg-purple-700">
            <Plus size={18} className="mr-2" />
            Create Offer
          </Button>
        </div>

        {offers.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm">
            <Gift size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-4">No promotional offers yet</p>
            <Button onClick={openAddModal} className="bg-purple-600 hover:bg-purple-700">
              Create Your First Offer
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <div 
                key={offer.id} 
                className={`bg-white rounded-xl overflow-hidden shadow-md ${!offer.is_active ? 'opacity-60' : ''}`}
              >
                {offer.flyer_image && (
                  <div className="h-48 bg-gray-100">
                    <img 
                      src={offer.flyer_image} 
                      alt={offer.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full mb-2">
                        {offer.occasion}
                      </span>
                      <h3 className="font-semibold text-gray-900">{offer.title}</h3>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      isOfferActive(offer) 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isOfferActive(offer) ? 'Live' : 'Inactive'}
                    </span>
                  </div>
                  
                  {offer.discount_text && (
                    <p className="text-lg font-bold text-red-600 mb-2">{offer.discount_text}</p>
                  )}
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{offer.description}</p>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <Calendar size={14} />
                    <span>{offer.valid_from} to {offer.valid_until}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-3 border-t">
                    <button
                      onClick={() => toggleActive(offer)}
                      className={`p-2 rounded-lg ${offer.is_active ? 'text-gray-500 hover:bg-gray-100' : 'text-green-600 hover:bg-green-50'}`}
                      title={offer.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {offer.is_active ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      onClick={() => openEditModal(offer)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(offer.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Offer' : 'Create New Offer'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Offer Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Diwali Special Offer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Occasion *</label>
                  <select
                    value={formData.occasion}
                    onChange={(e) => setFormData({ ...formData, occasion: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Occasion</option>
                    {OCCASIONS.map((occ) => (
                      <option key={occ} value={occ}>{occ}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount / Highlight Text</label>
                <input
                  type="text"
                  value={formData.discount_text}
                  onChange={(e) => setFormData({ ...formData, discount_text: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., 10% Off, Free Modular Kitchen, Gold Coin on Booking"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Describe the offer details..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flyer / Banner Image</label>
                <div className="space-y-3">
                  {formData.flyer_image ? (
                    <div className="relative inline-block">
                      <img 
                        src={formData.flyer_image} 
                        alt="Flyer Preview" 
                        className="max-w-full max-h-64 object-contain rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, flyer_image: '' })}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
                        <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          {uploadingImage ? 'Uploading...' : 'Click to upload flyer image'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG (Recommended: 800x600px)</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid From *</label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until *</label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Enquire Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link (Optional)</label>
                  <input
                    type="text"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="/contact or WhatsApp link"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                <textarea
                  value={formData.terms_conditions}
                  onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="*T&C Apply. Offer valid for limited period only."
                ></textarea>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_on_homepage}
                    onChange={(e) => setFormData({ ...formData, show_on_homepage: e.target.checked })}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm text-gray-700">Show on Homepage</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">
                  {editingId ? 'Update' : 'Create'} Offer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOffers;
